import { useAuth } from "../../contexts/AuthContext";
import { useEarningsVisibility } from "../../contexts/EarningsVisibilityContext";
import { dashboardRequest } from "../../services/dashboardRequest";
import { useDashboardResource } from "../../hooks/useDashboardResource";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import jsPDF from "jspdf";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { useDateFilter } from "../../contexts/DateFilterContext";
import { frameStatisticsTimestamp, statisticsRange, statisticsApiDate, formatStatisticsInput as formatDateTimeInput } from "../../utils/frameStatisticsDate";
import { formatAmount, formatAmountRaw, formatMoney, formatMoneyCode, getExcelMoneyFormat, roundMoney } from "../../utils/currency";
import { Link } from "react-router-dom";
import "../../assets/css/SnookerStats.css";
import DoubleCheckBadge from "../../Components/DoubleCheckBadge";

const StatsIcon = ({ name }) => {
    const common = {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.8",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        "aria-hidden": true,
        focusable: "false",
    };

    switch (name) {
        case "frames":
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="3" /><path d="M12 3.5v2M20.5 12h-2M12 20.5v-2M3.5 12h2" /></svg>;
        case "paid":
            return <svg {...common}><path d="M4 6.5h16v11H4z" /><path d="M7 10h5M7 14h3M16.5 10.5v3M15 12h3" /></svg>;
        case "given":
            return <svg {...common}><path d="M12 21s-7-4.35-7-11a4 4 0 0 1 7-2.65A4 4 0 0 1 19 10c0 6.65-7 11-7 11Z" /><path d="M9 12h6" /></svg>;
        case "earnings":
            return <svg {...common}><path d="M4 7.5h14a2 2 0 0 1 2 2v8H5a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2h12" /><path d="M15 11h5v4h-5a2 2 0 1 1 0-4Z" /></svg>;
        case "refresh":
            return <svg {...common}><path d="M20 7v5h-5M4 17v-5h5" /><path d="M6.1 8a7 7 0 0 1 11.4-1.7L20 9M4 15l2.5 2.7A7 7 0 0 0 18 16" /></svg>;
        case "export":
            return <svg {...common}><path d="M12 3v12M7 8l5-5 5 5M5 14v5h14v-5" /></svg>;
        case "calendar":
            return <svg {...common}><rect x="3.5" y="5.5" width="17" height="15" rx="3" /><path d="M8 3.5v4M16 3.5v4M3.5 10h17" /></svg>;
        case "grid":
            return <svg {...common}><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></svg>;
        case "table":
            return <svg {...common}><rect x="3.5" y="4" width="17" height="16" rx="2.5" /><path d="M3.5 9.5h17M9 4v16" /></svg>;
        case "eye":
            return <svg {...common}><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.5" /></svg>;
        case "eye-off":
            return <svg {...common}><path d="m3 3 18 18M10.6 6.1A9.4 9.4 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-2.1 2.8M6.6 6.7C4 8.3 2.5 12 2.5 12s3.5 6 9.5 6a9 9 0 0 0 3.1-.5M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>;
        case "chevron":
            return <svg {...common}><path d="m8 10 4 4 4-4" /></svg>;
        case "arrow":
            return <svg {...common}><path d="M5 12h14M14 7l5 5-5 5" /></svg>;
        default:
            return <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 8v4M12 16h.01" /></svg>;
    }
};

const formatDuration = (frame) => {
    const duration = typeof frame.duration_ms === "number" ? frame.duration_ms : (() => {
        const end = new Date(frame.created_at).getTime();
        const start = frame.frame_start_ts ? new Date(frame.frame_start_ts).getTime() : null;
        if (!start || Number.isNaN(start)) return null;
        return Math.max(0, end - start);
    })();

    if (duration === null) return "—";
    const totalSeconds = Math.floor(duration / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatRangeLabel = (value) => {
    if (!value) return "Not set";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const formatEarningsValue = (amount, currency, showEarnings) => {
    if (!showEarnings) return "••••";
    return currency ? formatMoney(amount, currency) : formatAmount(amount, currency);
};

const frameIsQualified = (frame) => frame?.qualifies !== false;
const getQualifiedFrames = (frames = []) => frames.filter(frameIsQualified);

const getQualificationCounts = (frames = []) => ({
    qualified: getQualifiedFrames(frames).length,
    notQualified: frames.filter(frame => !frameIsQualified(frame)).length,
});

const SnookerTableCards = ({
    label,
    tables,
    framesByTable,
    price,
    currency,
    showEarnings,
    showDetails,
    onToggleDetails,
    computeQualifyingCount,
}) => {
    if (!tables.length) return null;

    return (
        <section className="snooker-stats-table-section" aria-labelledby={`snooker-${label.toLowerCase().replace(/\s+/g, "-")}-cards`}>
            <div className="snooker-stats-section-heading">
                <div>
                    <span className="snooker-stats-section-kicker">Table performance</span>
                    <h2 id={`snooker-${label.toLowerCase().replace(/\s+/g, "-")}-cards`}>{label}</h2>
                </div>
                <span className="snooker-stats-section-count">{tables.length} {tables.length === 1 ? "table" : "tables"}</span>
            </div>

            <div className="snooker-stats-card-grid">
                {tables.map((table) => {
                    const frames = framesByTable[table.id] || [];
                    const frameCount = computeQualifyingCount(frames);
                    const { qualified, notQualified } = getQualificationCounts(frames);
                    const qualifiedFrames = getQualifiedFrames(frames);
                    const paidCount = qualifiedFrames.filter(frame => frame.paid).length;
                    const givenCount = qualifiedFrames.length - paidCount;
                    const recentFrames = [...frames]
                        .sort((a, b) => new Date(frameStatisticsTimestamp(b)) - new Date(frameStatisticsTimestamp(a)))
                        .slice(0, 6);

                    return (
                        <article className="snooker-stats-table-card" key={table.id}>
                            <header className="snooker-stats-table-card-header">
                                <div className="snooker-stats-table-identity">
                                    <span className="snooker-stats-table-orbit"><i /></span>
                                    <div>
                                        <span>{table.pricing_type === "mini" ? "Mini rate" : "Standard rate"}</span>
                                        <h3>{table.name}</h3>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="snooker-stats-detail-toggle"
                                    onClick={onToggleDetails}
                                    aria-expanded={showDetails}
                                >
                                    {showDetails ? "Hide activity" : "Show activity"}
                                    <StatsIcon name="chevron" />
                                </button>
                            </header>

                            <dl className="snooker-stats-card-metrics">
                                <div><dt>Frames</dt><dd>{frameCount}</dd></div>
                                <div><dt>Paid</dt><dd>{paidCount}</dd></div>
                                <div><dt>Given</dt><dd>{givenCount}</dd></div>
                                <div className="is-earnings"><dt>Takings</dt><dd>{formatEarningsValue(paidCount * price, currency, showEarnings)}</dd></div>
                            </dl>

                            {showDetails && (
                                <div className="snooker-stats-recent-frames">
                                    <div className="snooker-stats-recent-heading">
                                        <span>Latest recorded frames</span>
                                        <span>{qualified} qualified · {notQualified} not qualified</span>
                                    </div>
                                    {recentFrames.length ? (
                                        <div className="snooker-stats-frame-list">
                                            {recentFrames.map((frame, index) => (
                                                <div className="snooker-stats-frame-row" key={frame.id || `${table.id}-${frame.created_at}-${index}`}>
                                                    <span className="snooker-stats-frame-dot" />
                                                    <div>
                                                        <strong title={`Saved: ${new Date(frame.created_at).toLocaleString()}`}>{new Date(frameStatisticsTimestamp(frame)).toLocaleString()}</strong>
                                                        <span>Duration {formatDuration(frame)}</span>
                                                        <DoubleCheckBadge qualification={frame.qualification} />
                                                    </div>
                                                    <span className={`snooker-stats-qualification ${frameIsQualified(frame) ? "is-qualified" : "is-not-qualified"}`}>
                                                        {frameIsQualified(frame) ? "Qualified" : "Not qualified"}
                                                    </span>
                                                    <span className="snooker-stats-action-count">{frame.actions_count ?? "—"} actions</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="snooker-stats-no-frames">No recorded frames in this period.</p>
                                    )}
                                </div>
                            )}

                            <Link to={`/table/stats/${table.id}`} className="snooker-stats-table-link">
                                View table details <StatsIcon name="arrow" />
                            </Link>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

const SnookerTableList = ({
    label,
    tables,
    framesByTable,
    price,
    currency,
    showEarnings,
    computeQualifyingCount,
}) => {
    if (!tables.length) return null;

    const groupTotals = tables.reduce((result, table) => {
        const frames = framesByTable[table.id] || [];
        const qualifiedFrames = getQualifiedFrames(frames);
        const paid = qualifiedFrames.filter(frame => frame.paid).length;
        const { qualified, notQualified } = getQualificationCounts(frames);
        result.frames += computeQualifyingCount(frames);
        result.qualified += qualified;
        result.notQualified += notQualified;
        result.paid += paid;
        result.given += qualifiedFrames.length - paid;
        result.earnings += paid * price;
        return result;
    }, { frames: 0, qualified: 0, notQualified: 0, paid: 0, given: 0, earnings: 0 });

    const headingId = `snooker-${label.toLowerCase().replace(/\s+/g, "-")}-table`;

    return (
        <section className="snooker-stats-table-section" aria-labelledby={headingId}>
            <div className="snooker-stats-section-heading">
                <div>
                    <span className="snooker-stats-section-kicker">Table performance</span>
                    <h2 id={headingId}>{label}</h2>
                </div>
                <span className="snooker-stats-section-count">{tables.length} {tables.length === 1 ? "table" : "tables"}</span>
            </div>

            <div className="snooker-stats-table-shell">
                <div className="snooker-stats-table-scroll">
                    <table className="snooker-stats-data-table">
                        <caption className="visually-hidden">{label} performance for the selected period</caption>
                        <thead>
                            <tr>
                                <th scope="col">Table</th>
                                <th scope="col">Frames</th>
                                <th scope="col">Qualified</th>
                                <th scope="col">Not qualified</th>
                                <th scope="col">Paid</th>
                                <th scope="col">Given</th>
                                <th scope="col">Takings</th>
                                <th scope="col"><span className="visually-hidden">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tables.map((table) => {
                                const frames = framesByTable[table.id] || [];
                                const framesCount = computeQualifyingCount(frames);
                                const { qualified, notQualified } = getQualificationCounts(frames);
                                const qualifiedFrames = getQualifiedFrames(frames);
                                const paid = qualifiedFrames.filter(frame => frame.paid).length;
                                const given = qualifiedFrames.length - paid;
                                return (
                                    <tr key={table.id}>
                                        <th scope="row">
                                            <span className="snooker-stats-table-name">{table.name}</span>
                                            <span className="snooker-stats-pricing-label">{table.pricing_type === "mini" ? "Mini" : "Standard"}</span>
                                        </th>
                                        <td data-label="Frames"><strong>{framesCount}</strong></td>
                                        <td data-label="Qualified"><span className="snooker-stats-status-value is-qualified">{qualified}</span></td>
                                        <td data-label="Not qualified"><span className="snooker-stats-status-value is-not-qualified">{notQualified}</span></td>
                                        <td data-label="Paid"><span className="snooker-stats-status-value is-paid">{paid}</span></td>
                                        <td data-label="Given"><span className="snooker-stats-status-value is-given">{given}</span></td>
                                        <td data-label="Takings" className="snooker-stats-earnings-cell">{formatEarningsValue(paid * price, currency, showEarnings)}</td>
                                        <td>
                                            <Link to={`/table/stats/${table.id}`} className="snooker-stats-row-link" aria-label={`View statistics for ${table.name}`}>
                                                View <StatsIcon name="arrow" />
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th scope="row">Group total</th>
                                <td data-label="Frames">{groupTotals.frames}</td>
                                <td data-label="Qualified">{groupTotals.qualified}</td>
                                <td data-label="Not qualified">{groupTotals.notQualified}</td>
                                <td data-label="Paid">{groupTotals.paid}</td>
                                <td data-label="Given">{groupTotals.given}</td>
                                <td data-label="Takings">{formatEarningsValue(groupTotals.earnings, currency, showEarnings)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </section>
    );
};

const SnookerStats = () => {
    const { user } = useAuth();
    const userId = user?.id;
    const { showEarnings, toggleEarningsVisibility } = useEarningsVisibility();
    const { from, to, setFrom, setTo } = useDateFilter();
    const [showDetails, setShowDetails] = useState(true);
    const [exportOpen, setExportOpen] = useState(false);
    const exportRef = useRef(null);
    const [viewMode, setViewMode] = useState('table');
    const setRangeFor = useCallback((baseDate) => {
        const { start, end } = statisticsRange(baseDate);
        setFrom(formatDateTimeInput(start));
        setTo(formatDateTimeInput(end));
    }, [setFrom, setTo]);
    const setTodayRange = useCallback(() => setRangeFor(new Date()), [setRangeFor]);
    const setYesterdayRange = useCallback(() => {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        setRangeFor(date);
    }, [setRangeFor]);
    const loadSnapshot = useCallback(async ({ signal }) => {
        const [tableData, settingData] = await Promise.all([
            dashboardRequest('/club/tables', { method: 'POST', body: { id: userId }, signal }),
            dashboardRequest(`/settings/${userId}`, { signal }),
        ]);
        if (!Array.isArray(tableData.tables) || !settingData.settings
            || typeof settingData.settings !== 'object' || Array.isArray(settingData.settings)) {
            throw new Error('The server returned incomplete snooker activity. Please retry.');
        }
        const snookerTables = tableData.tables.filter(t => (t.type || '').toLowerCase() === 'snooker');
        const results = await Promise.all(snookerTables.map(async (t) => {
            const res = await dashboardRequest('/club/table/frames', { method: 'POST', signal, body: {
                id_table: t.id,
                from: statisticsApiDate(from),
                to: statisticsApiDate(to),
                id_user: userId,
                qualifying_only: false,
            } });
            if (!Array.isArray(res.frames)) throw new Error(`Activity for ${t.name || 'a table'} is unavailable. Please retry.`);
            return { tableId: t.id, frames: res.frames };
        }));
        const map = {};
        results.forEach(r => { map[r.tableId] = r.frames; });
        const settings = settingData.settings;
        const snPrice = parseFloat(settings.snooker_pricing_per_game);
        const miniPrice = parseFloat(settings.mini_snooker_pricing_per_game);
        return {
            tables: snookerTables, framesByTable: map,
            snookerPrice: Number.isFinite(snPrice) ? snPrice : 0,
            miniSnookerPrice: Number.isFinite(miniPrice) ? miniPrice : 0,
            currency: settings.default_currency || '',
        };
    }, [from, to, userId]);
    const { data: snapshot, loading, refreshing, error: loadError, refresh } = useDashboardResource({
        key: JSON.stringify([userId, from, to]),
        enabled: Boolean(userId && from && to),
        load: loadSnapshot,
    });
    const { tables = [], framesByTable = {}, snookerPrice = 0, miniSnookerPrice = 0, currency = '' } = snapshot || {};
    const standardTables = useMemo(() => tables.filter(t => t.pricing_type !== 'mini'), [tables]);
    const miniTables = useMemo(() => tables.filter(t => t.pricing_type === 'mini'), [tables]);

    useEffect(() => {
        if (!from || !to) {
            setTodayRange();
        }
    }, [from, to, setTodayRange]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (exportOpen && exportRef.current && !exportRef.current.contains(e.target)) {
                setExportOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape" && exportOpen) {
                setExportOpen(false);
                exportRef.current?.querySelector(".snooker-stats-export-button")?.focus();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [exportOpen]);

    const computeQualifyingCount = useCallback((frames) => {
        return getQualifiedFrames(frames || []).length;
    }, []);

    const totals = useMemo(() => {
        let totalRecorded = 0;
        let totalSaved = 0;
        let totalQualified = 0;
        let totalNotQualified = 0;
        let totalPaid = 0;
        let totalEarnings = 0;
        tables.forEach(t => {
            const frames = framesByTable[t.id] || [];
            const qualifiedFrames = getQualifiedFrames(frames);
            totalSaved += frames.length;
            totalRecorded += computeQualifyingCount(frames);
            const qualification = getQualificationCounts(frames);
            totalQualified += qualification.qualified;
            totalNotQualified += qualification.notQualified;
            const paid = qualifiedFrames.filter(f => f.paid).length;
            totalPaid += paid;
            const price = t && t.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice;
            totalEarnings += paid * price;
        });
        return {
            totalRecorded,
            totalSaved,
            totalQualified,
            totalNotQualified,
            totalPaid,
            totalGiven: Math.max(0, totalQualified - totalPaid),
            totalEarnings,
            collectionRate: totalQualified > 0 ? Math.round((totalPaid / totalQualified) * 100) : 0,
        };
    }, [tables, framesByTable, computeQualifyingCount, snookerPrice, miniSnookerPrice]);

    const csvSafe = (val) => {
        const s = String(val ?? "");
        const needsQuote = s.includes(",") || s.includes("\"") || s.includes("\n") || s.includes("\r");
        const escaped = s.replace(/"/g, '""');
        return needsQuote ? `"${escaped}"` : s;
    };

    const downloadCSV = () => {
        const headers = ["Table", "Pricing Type", "Frames", "Qualified", "Not qualified", "Paid", "Given", "Earnings", "Currency", "From", "To"];
        let totalFrames = 0;
        let totalQualified = 0;
        let totalNotQualified = 0;
        let totalPaid = 0;
        let totalGiven = 0;
        let totalEarnings = 0;
        const rows = tables.map(t => {
            const frames = framesByTable[t.id] || [];
            const qualifying = computeQualifyingCount(frames);
            const qualification = getQualificationCounts(frames);
            const qualifiedFrames = getQualifiedFrames(frames);
            const paid = qualifiedFrames.filter(f => f.paid).length;
            const given = qualifiedFrames.length - paid;
            const price = t && t.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice;
            const earnings = formatAmountRaw(paid * price, currency);
            totalFrames += qualifying;
            totalQualified += qualification.qualified;
            totalNotQualified += qualification.notQualified;
            totalPaid += paid;
            totalGiven += given;
            totalEarnings += parseFloat(earnings);
            return [
                csvSafe(t.name),
                csvSafe(t && t.pricing_type === 'mini' ? 'Mini' : 'Standard'),
                csvSafe(qualifying),
                csvSafe(qualification.qualified),
                csvSafe(qualification.notQualified),
                csvSafe(paid),
                csvSafe(given),
                csvSafe(showEarnings ? earnings : "****"),
                csvSafe(currency || ""),
                csvSafe(from || ""),
                csvSafe(to || "")
            ];
        });
        const totalRow = [
            csvSafe("Total"),
            csvSafe(""),
            csvSafe(totalFrames),
            csvSafe(totalQualified),
            csvSafe(totalNotQualified),
            csvSafe(totalPaid),
            csvSafe(totalGiven),
            csvSafe(showEarnings ? formatAmountRaw(totalEarnings, currency) : "****"),
            csvSafe(currency || ""),
            csvSafe(from || ""),
            csvSafe(to || "")
        ];
        const csvBody = [headers.join(","), ...rows.map(r => r.join(",")), totalRow.join(",")].join("\r\n");
        const csvContent = "\uFEFF" + csvBody;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const dateStr = new Date().toISOString().split("T")[0];
        a.download = `snooker_stats_${dateStr}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 10;
        const lineHeight = 7;
        let y = 20;

        doc.setFontSize(16);
        const title = "Snooker Tables Statistics";
        const tw = doc.getTextWidth(title);
        doc.text(title, (pageWidth - tw) / 2, y);
        y += 10;

        doc.setFontSize(10);
        const rangeText = `Range: ${from || '-'} to ${to || '-'}`;
        doc.text(rangeText, margin, y);
        y += 8;

        const headers = ["Table", "Pricing", "Frames", "Qualified", "Not qual.", "Paid", "Given", "Earnings"];
        const colWidths = [55, 30, 25, 30, 30, 25, 25, 57];
        let x = margin;

        doc.setFillColor(0, 128, 0);
        doc.rect(margin, y - lineHeight, pageWidth - 2 * margin, lineHeight, 'F');
        doc.setTextColor(255, 255, 255);
        headers.forEach((h, i) => {
            doc.text(h, x + 2, y - 2);
            x += colWidths[i];
        });
        y += lineHeight;
        doc.setTextColor(0, 0, 0);

        const drawRow = (cells) => {
            let rx = margin;
            cells.forEach((c, i) => {
                doc.text(String(c), rx + 2, y - 2);
                rx += colWidths[i];
            });
            y += lineHeight;
            doc.line(margin, y - lineHeight, pageWidth - margin, y - lineHeight);
        };

        let totalFrames = 0;
        let totalQualified = 0;
        let totalNotQualified = 0;
        let totalPaid = 0;
        let totalGiven = 0;
        let totalEarnings = 0;
        tables.forEach(t => {
            if (y > pageHeight - 20) {
                doc.addPage();
                y = 20;
            }
            const frames = framesByTable[t.id] || [];
            const qualifying = computeQualifyingCount(frames);
            const qualification = getQualificationCounts(frames);
            const qualifiedFrames = getQualifiedFrames(frames);
            const paid = qualifiedFrames.filter(f => f.paid).length;
            const given = qualifiedFrames.length - paid;
            const price = t && t.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice;
            const earnings = roundMoney(paid * price, currency);
            totalFrames += qualifying;
            totalQualified += qualification.qualified;
            totalNotQualified += qualification.notQualified;
            totalPaid += paid;
            totalGiven += given;
            totalEarnings += earnings;
            drawRow([
                t.name,
                t && t.pricing_type === 'mini' ? 'Mini' : 'Standard',
                qualifying,
                qualification.qualified,
                qualification.notQualified,
                paid,
                given,
                showEarnings ? (currency ? formatMoneyCode(earnings, currency) : formatAmount(earnings, currency)) : '****'
            ]);
        });

        if (y > pageHeight - 20) {
            doc.addPage();
            y = 20;
        }
        doc.setFontSize(10);
        drawRow([
            'Total',
            '',
            totalFrames,
            totalQualified,
            totalNotQualified,
            totalPaid,
            totalGiven,
            showEarnings ? (currency ? formatMoneyCode(totalEarnings, currency) : formatAmount(totalEarnings, currency)) : '****'
        ]);

        doc.save(`snooker_stats_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const downloadXLSX = async () => {
        const wb = new ExcelJS.Workbook();
        wb.creator = 'Scoreboard';
        wb.created = new Date();
        const ws = wb.addWorksheet('Snooker Stats');

        const tablesList = tables.map(t => ({ id: t.id, name: t.name }));
        const numeric = (v) => (typeof v === 'number' ? v : Number(v));

        const headerRow = ['Field', ...tablesList.map(t => t.name), 'Total'];
        ws.addRow(headerRow);

        const totals = { frames: 0, qualified: 0, notQualified: 0, paid: 0, given: 0, earnings: 0 };
        const makeRow = (label, values, total) => {
            ws.addRow([label, ...values, total]);
        };

        const framesRow = tablesList.map(t => {
            const frames = framesByTable[t.id] || [];
            const count = computeQualifyingCount(frames);
            totals.frames += count;
            return count;
        });
        makeRow('Frames', framesRow, totals.frames);

        const qualifiedRow = tablesList.map(t => {
            const count = getQualificationCounts(framesByTable[t.id] || []).qualified;
            totals.qualified += count;
            return count;
        });
        makeRow('Qualified', qualifiedRow, totals.qualified);

        const notQualifiedRow = tablesList.map(t => {
            const count = getQualificationCounts(framesByTable[t.id] || []).notQualified;
            totals.notQualified += count;
            return count;
        });
        makeRow('Not qualified', notQualifiedRow, totals.notQualified);

        const paidRow = tablesList.map(t => {
            const frames = framesByTable[t.id] || [];
            const count = getQualifiedFrames(frames).filter(f => f.paid).length;
            totals.paid += count;
            return count;
        });
        makeRow('Paid', paidRow, totals.paid);

        const givenRow = tablesList.map(t => {
            const frames = framesByTable[t.id] || [];
            const qualifiedFrames = getQualifiedFrames(frames);
            const count = qualifiedFrames.length - qualifiedFrames.filter(f => f.paid).length;
            totals.given += count;
            return count;
        });
        makeRow('Given', givenRow, totals.given);

        const earningsRow = tablesList.map(t => {
            const frames = framesByTable[t.id] || [];
            const paid = getQualifiedFrames(frames).filter(f => f.paid).length;
            const price = (tables.find(tt => tt.id === t.id)?.pricing_type === 'mini') ? miniSnookerPrice : snookerPrice;
            const amount = paid * price;
            totals.earnings += amount;
            return showEarnings ? roundMoney(amount, currency) : null;
        });
        makeRow('Earnings', earningsRow, showEarnings ? roundMoney(totals.earnings, currency) : null);

        const pricingRow = tablesList.map(t => {
            const pt = tables.find(tt => tt.id === t.id)?.pricing_type;
            return pt === 'mini' ? 'Mini' : 'Standard';
        });
        makeRow('Pricing Type', pricingRow, '');
        makeRow('Currency', tablesList.map(() => currency || ''), currency || '');
        makeRow('From', tablesList.map(() => from || ''), from || '');
        makeRow('To', tablesList.map(() => to || ''), to || '');

        ws.columns = headerRow.map((_, i) => ({ width: i === 0 ? 18 : 14 }));

        const header = ws.getRow(1);
        header.font = { bold: true };
        header.alignment = { vertical: 'middle', horizontal: 'center' };
        header.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2B7A0B' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        });

        ws.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;
            row.alignment = { vertical: 'middle', horizontal: 'center' };
            row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
            row.eachCell((cell) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const earningsRowIdx = 1 + 6; // Field, Frames, qualification rows, Paid, Given, Earnings
        const lastCol = headerRow.length;
        const earningsRange = `${colLetter(2)}${earningsRowIdx}:${colLetter(lastCol)}${earningsRowIdx}`;
        try {
            ws.addConditionalFormatting({
                ref: earningsRange,
                rules: [
                    { type: 'greaterThan', formulae: ['0'], style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' }, fgColor: { argb: 'FFC6EFCE' } } } },
                ],
            });
        } catch (e) {
            // Fallback: style positive earnings directly
            for (let c = 2; c <= lastCol; c++) {
                const cell = ws.getCell(earningsRowIdx, c);
                const v = numeric(cell.value);
                if (typeof v === 'number' && v > 0) {
                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC6EFCE' } };
                }
            }
        }

        // Data validation: numeric rows
        const numericRows = ['Frames', 'Qualified', 'Not qualified', 'Paid', 'Given', 'Earnings'];
        numericRows.forEach((label, idx) => {
            const r = 1 + (idx + 1);
            for (let c = 2; c <= lastCol; c++) {
                const cell = ws.getCell(r, c);
                cell.dataValidation = { type: 'decimal', operator: 'greaterThanOrEqual', formulae: [0], showErrorMessage: true };
                if (label !== 'Earnings') cell.numFmt = '0';
                if (label === 'Earnings') cell.numFmt = getExcelMoneyFormat(currency);
            }
        });

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `snooker_stats_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadXLS = () => {
        const header = ['Table', 'Pricing', 'Frames', 'Qualified', 'Not qualified', 'Paid', 'Given', 'Earnings', 'Currency', 'From', 'To'];
        const data = [header];

        let totalFrames = 0;
        let totalQualified = 0;
        let totalNotQualified = 0;
        let totalPaid = 0;
        let totalGiven = 0;
        let totalEarnings = 0;

        tables.forEach((t) => {
            const frames = framesByTable[t.id] || [];
            const framesCount = computeQualifyingCount(frames);
            const qualification = getQualificationCounts(frames);
            const qualifiedFrames = getQualifiedFrames(frames);
            const paidCount = qualifiedFrames.filter(f => f.paid).length;
            const givenCount = qualifiedFrames.length - paidCount;
            const price = t && t.pricing_type === 'mini' ? miniSnookerPrice : snookerPrice;
            const earningsVal = paidCount * price;

            totalFrames += framesCount;
            totalQualified += qualification.qualified;
            totalNotQualified += qualification.notQualified;
            totalPaid += paidCount;
            totalGiven += givenCount;
            totalEarnings += earningsVal;

            data.push([
                t.name,
                t && t.pricing_type === 'mini' ? 'Mini' : 'Standard',
                framesCount,
                qualification.qualified,
                qualification.notQualified,
                paidCount,
                givenCount,
                showEarnings ? roundMoney(earningsVal, currency) : '',
                currency || '',
                from || '',
                to || ''
            ]);
        });

        data.push([
            'Total',
            '',
            totalFrames,
            totalQualified,
            totalNotQualified,
            totalPaid,
            totalGiven,
            showEarnings ? roundMoney(totalEarnings, currency) : '',
            currency || '',
            from || '',
            to || ''
        ]);

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Snooker Stats');
        XLSX.writeFile(wb, `snooker_stats_${new Date().toISOString().split('T')[0]}.xls`, { bookType: 'xls' });
    };

    if (loading) {
        return (
            <main className="snooker-stats-page snooker-stats-loading" aria-busy="true">
                <div className="snooker-stats-loading-inner">
                    <span className="snooker-stats-loading-mark" aria-hidden="true" />
                    <span>Preparing snooker activity…</span>
                </div>
            </main>
        );
    }

    if (!snapshot) {
        return <main className="snooker-stats-page snooker-stats-empty">
            <h1>Snooker activity is unavailable</h1>
            <p role="alert">{loadError || 'Refresh to try loading snooker activity again.'}</p>
            <button type="button" className="btn btn-primary" onClick={refresh}>Try again</button>
        </main>;
    }

    const periodLabel = `${formatRangeLabel(from)} — ${formatRangeLabel(to)}`;

    return (
        <main className="snooker-stats-page">
            <header className="snooker-stats-hero">
                <div className="snooker-stats-container snooker-stats-hero-inner">
                    <div className="snooker-stats-hero-copy">
                        <span className="snooker-stats-eyebrow">Snooker performance</span>
                        <h1>See how every table is performing.</h1>
                        <p>Review every saved frame, its qualification status, payment status and estimated takings across your snooker floor.</p>
                    </div>
                    <div className="snooker-stats-period-summary" aria-label={`Selected period: ${periodLabel}`}>
                        <StatsIcon name="calendar" />
                        <div>
                            <span>Selected period</span>
                            <strong>{periodLabel}</strong>
                        </div>
                    </div>
                </div>
            </header>

            <div className="snooker-stats-container">
                <section className="snooker-stats-toolbar" aria-label="Snooker statistics filters">
                    <div className="snooker-stats-range-controls">
                        <div className="snooker-stats-date-field">
                            <label htmlFor="snooker-stats-from">From</label>
                            <input id="snooker-stats-from" type="datetime-local" value={from} onChange={(event) => setFrom(event.target.value)} />
                        </div>
                        <div className="snooker-stats-date-field">
                            <label htmlFor="snooker-stats-to">To</label>
                            <input id="snooker-stats-to" type="datetime-local" value={to} onChange={(event) => setTo(event.target.value)} />
                        </div>
                        <div className="snooker-stats-presets" aria-label="Quick date ranges">
                            <button type="button" className="snooker-stats-preset" onClick={setTodayRange}>Today</button>
                            <button type="button" className="snooker-stats-preset" onClick={setYesterdayRange}>Yesterday</button>
                        </div>
                    </div>

                    <div className="snooker-stats-toolbar-actions">
                        <button
                            type="button"
                            className={`snooker-stats-icon-button${refreshing ? " is-refreshing" : ""}`}
                            onClick={refresh}
                            disabled={refreshing}
                            aria-label={refreshing ? "Refreshing statistics" : "Refresh statistics"}
                            title="Refresh statistics"
                        >
                            <StatsIcon name="refresh" />
                        </button>

                        <div className="snooker-stats-export" ref={exportRef}>
                            <button
                                type="button"
                                className="snooker-stats-export-button"
                                onClick={() => setExportOpen(open => !open)}
                                aria-haspopup="menu"
                                aria-expanded={exportOpen}
                            >
                                <StatsIcon name="export" /> Export <span className="snooker-stats-chevron"><StatsIcon name="chevron" /></span>
                            </button>
                            {exportOpen && (
                                <div className="snooker-stats-export-menu" role="menu" aria-label="Export format">
                                    <button type="button" role="menuitem" onClick={() => { setExportOpen(false); downloadPDF(); }}>PDF report <span>PDF</span></button>
                                    <button type="button" role="menuitem" onClick={() => { setExportOpen(false); downloadXLSX(); }}>Excel workbook <span>XLSX</span></button>
                                    <button type="button" role="menuitem" onClick={() => { setExportOpen(false); downloadXLS(); }}>Legacy Excel <span>XLS</span></button>
                                    <button type="button" role="menuitem" onClick={() => { setExportOpen(false); downloadCSV(); }}>Comma-separated data <span>CSV</span></button>
                                </div>
                            )}
                        </div>

                        <div className="snooker-stats-view-switch" role="group" aria-label="Statistics view">
                            <button type="button" className={viewMode === "cards" ? "is-active" : ""} onClick={() => setViewMode("cards")} aria-pressed={viewMode === "cards"} aria-label="Cards view" title="Cards view"><StatsIcon name="grid" /></button>
                            <button type="button" className={viewMode === "table" ? "is-active" : ""} onClick={() => setViewMode("table")} aria-pressed={viewMode === "table"} aria-label="Table view" title="Table view"><StatsIcon name="table" /></button>
                        </div>
                    </div>
                </section>

                {loadError && <div className="snooker-stats-notice" role="alert"><StatsIcon name="info" /> Could not refresh snooker activity. Your last available figures for this period are still shown. {loadError}</div>}

                <section className="snooker-stats-metrics" aria-label="Snooker summary">
                    <article className="snooker-stats-metric" aria-label="Recorded frames summary">
                        <div className="snooker-stats-metric-top"><span className="snooker-stats-metric-icon"><StatsIcon name="frames" /></span></div>
                        <span className="snooker-stats-metric-label">Recorded frames</span>
                        <strong>{totals.totalRecorded}</strong>
                        <small>{totals.totalSaved} recorded · {totals.totalNotQualified} not qualified</small>
                    </article>
                    <article className="snooker-stats-metric">
                        <div className="snooker-stats-metric-top"><span className="snooker-stats-metric-icon is-paid"><StatsIcon name="paid" /></span></div>
                        <span className="snooker-stats-metric-label">Paid frames</span>
                        <strong>{totals.totalPaid}</strong>
                        <small>{totals.collectionRate}% of qualified frames</small>
                    </article>
                    <article className="snooker-stats-metric">
                        <div className="snooker-stats-metric-top"><span className="snooker-stats-metric-icon is-given"><StatsIcon name="given" /></span></div>
                        <span className="snooker-stats-metric-label">Given frames</span>
                        <strong>{totals.totalGiven}</strong>
                        <small>Qualified frames without a paid charge</small>
                    </article>
                    <article className="snooker-stats-metric is-featured" aria-label="Estimated takings summary">
                        <div className="snooker-stats-metric-top">
                            <span className="snooker-stats-metric-icon"><StatsIcon name="earnings" /></span>
                            <button type="button" className="snooker-stats-earnings-toggle" onClick={toggleEarningsVisibility} aria-label={showEarnings ? "Hide estimated takings" : "Show estimated takings"} title={showEarnings ? "Hide estimated takings" : "Show estimated takings"}><StatsIcon name={showEarnings ? "eye" : "eye-off"} /></button>
                        </div>
                        <span className="snooker-stats-metric-label">Estimated takings</span>
                        <strong>{formatEarningsValue(totals.totalEarnings, currency, showEarnings)}</strong>
                        <small>Qualified paid frames × current table rates</small>
                    </article>
                </section>

                {viewMode === "cards" ? (
                    <>
                        <SnookerTableCards label="Standard snooker" tables={standardTables} framesByTable={framesByTable} price={snookerPrice} currency={currency} showEarnings={showEarnings} showDetails={showDetails} onToggleDetails={() => setShowDetails(previous => !previous)} computeQualifyingCount={computeQualifyingCount} />
                        <SnookerTableCards label="Mini snooker" tables={miniTables} framesByTable={framesByTable} price={miniSnookerPrice} currency={currency} showEarnings={showEarnings} showDetails={showDetails} onToggleDetails={() => setShowDetails(previous => !previous)} computeQualifyingCount={computeQualifyingCount} />
                    </>
                ) : (
                    <>
                        <SnookerTableList label="Standard snooker" tables={standardTables} framesByTable={framesByTable} price={snookerPrice} currency={currency} showEarnings={showEarnings} computeQualifyingCount={computeQualifyingCount} />
                        <SnookerTableList label="Mini snooker" tables={miniTables} framesByTable={framesByTable} price={miniSnookerPrice} currency={currency} showEarnings={showEarnings} computeQualifyingCount={computeQualifyingCount} />
                    </>
                )}

                {!tables.length && (
                    <section className="snooker-stats-empty">
                        <span className="snooker-stats-empty-icon"><StatsIcon name="table" /></span>
                        <h2>No snooker tables yet</h2>
                        <p>Add a snooker table to start seeing its activity here.</p>
                    </section>
                )}
            </div>

            {refreshing && <div className="snooker-stats-refresh-status" role="status"><StatsIcon name="refresh" /> Updating figures…</div>}
        </main>
    );
};

export default SnookerStats;
const colLetter = (n) => {
    let s = '';
    while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
    return s;
};
